/**
 * Build-baked expectations for the Browser model store (GLOSSARY.md).
 *
 * The version keys change only when the vendored model bytes change; the size
 * table is regenerated by `scripts/fetch-model.mjs` and
 * `scripts/fetch-uform-text-model.mjs` after vendoring so a warm IndexedDB
 * entry can be validated without any network fetch. A stored entry whose
 * version or byte size differs from this manifest is stale and must be
 * refetched.
 *
 * All runtime model fetches stay same-origin (`/model/...`) — never a public
 * CDN at runtime. Only the build-time vendor step touches HuggingFace.
 */

/** Version key for the UForm text-encoder files (tokenizer + text_encoder.onnx). */
export const UFORM_MODEL_VERSION = "uform3-text-1";

/** Version key for the EmbeddingGemma files (tokenizer + q8 ONNX weights). */
export const EMBEDDINGGEMMA_MODEL_VERSION = "embeddinggemma-300m-q8-1";

/** Same-origin directory of the vendored UForm text-encoder files. */
export const UFORM_MODEL_DIR = "/model/uform3-image-text-multilingual-base";

/** Same-origin directory of the vendored EmbeddingGemma files. */
export const EMBEDDINGGEMMA_MODEL_DIR = "/model/embeddinggemma-300m";

/**
 * Expected byte size per model file, keyed by its same-origin URL path.
 * Regenerated by the fetch scripts; used to detect stale store entries
 * without a network round-trip (warm hit must perform no `/model/...` fetch).
 */
export const MODEL_FILE_SIZES: Record<string, number> = {
  "/model/uform3-image-text-multilingual-base/tokenizer.json": 24083970,
  "/model/uform3-image-text-multilingual-base/text_encoder.onnx": 110689743,
  "/model/embeddinggemma-300m/config.json": 1765,
  "/model/embeddinggemma-300m/tokenizer.json": 20323312,
  "/model/embeddinggemma-300m/tokenizer_config.json": 1156830,
  "/model/embeddinggemma-300m/special_tokens_map.json": 662,
  "/model/embeddinggemma-300m/onnx/model_quantized.onnx": 567874,
  "/model/embeddinggemma-300m/onnx/model_quantized.onnx_data": 308890624,
};

/** Model version key for a same-origin model URL, or null when not a known model file. */
export function modelVersionForKey(key: string): string | null {
  const path = key.split("?")[0] ?? key;
  if (path.startsWith(`${UFORM_MODEL_DIR}/`)) return UFORM_MODEL_VERSION;
  if (path.startsWith(`${EMBEDDINGGEMMA_MODEL_DIR}/`)) return EMBEDDINGGEMMA_MODEL_VERSION;
  // transformers.js also caches under its remote URL form; map those back to
  // the same version so the stale check applies regardless of key shape.
  if (path.includes("embeddinggemma-300m")) return EMBEDDINGGEMMA_MODEL_VERSION;
  if (path.includes("uform3-image-text-multilingual-base")) return UFORM_MODEL_VERSION;
  return null;
}

/** Build-baked expected byte size for a model URL, if known. */
export function expectedSizeForKey(key: string): number | undefined {
  const path = cacheKeyToModelPath(key);
  if (path && path in MODEL_FILE_SIZES) return MODEL_FILE_SIZES[path];
  return undefined;
}

/**
 * Normalize a transformers.js cache key (local `/model/...` path or remote
 * URL form) to its same-origin model path, or null when not a model file.
 */
export function cacheKeyToModelPath(key: string): string | null {
  const raw = key.split("?")[0] ?? key;
  const modelMarker = "/model/";
  const idx = raw.indexOf(modelMarker);
  if (idx === -1) return null;
  const path = raw.slice(idx);
  if (path.startsWith(`${UFORM_MODEL_DIR}/`) || path.startsWith(`${EMBEDDINGGEMMA_MODEL_DIR}/`)) {
    return path;
  }
  return null;
}
